import React, { useRef, useState, useEffect } from 'react';
import { useAnimationFrame } from '../hooks/useAnimationFrame';
import ActivityLED from './ActivityLED';
import { VISUAL_LATENCY_BEATS } from '../constants';

const TICKS_PER_BEAT = 192;

// Visual Grid Overlay Component
const GridOverlay = ({ structure, totalBeats }) => {
    if (!structure || totalBeats <= 0) return null;

    const lines = [];
    let currentBeat = 0;

    structure.forEach((section, idx) => {
        const timeSig = section.timeSignature || { numerator: 4, denominator: 4 };
        const num = timeSig.numerator;
        const bars = section.bars || 4;
        const sectionBeats = bars * num;

        for (let b = 0; b < sectionBeats; b++) {
             const isBarLine = (b % num === 0);
             const globalBeat = currentBeat + b;

             // Calculate position percentage
             const pct = (globalBeat / totalBeats) * 100;

             if (globalBeat >= 0 && globalBeat < totalBeats) {
                 lines.push(
                     <div key={`grid-${idx}-${b}`} style={{
                         position: 'absolute',
                         left: `${pct}%`,
                         top: 0,
                         bottom: 0,
                         width: isBarLine ? '1px' : '1px',
                         backgroundColor: isBarLine ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)',
                         pointerEvents: 'none'
                     }} />
                 );
             }
        }
        currentBeat += sectionBeats;
    });

    return (
        <div style={{
            position: 'absolute',
            top: 0, bottom: 0, left: '200px', right: 0,
            zIndex: 0,
            pointerEvents: 'none',
            overflow: 'hidden'
        }}>
            {lines}
        </div>
    );
};

const TrackLane = ({ instrument, events, totalBeats, onMute, onSolo, engine }) => {
    const [lastTrigger, setLastTrigger] = useState(0);

    // Register Note Listener for Visual Feedback
    useEffect(() => {
        if (!engine || !engine.registerNoteListener) return;

        const cleanup = engine.registerNoteListener((id, note) => {
             // Filter Keyswitches (Low/High Control Notes)
             if (note.d < 24 || note.d > 108) return;
             if (note.isKeyswitch) return;

             // Check for match (Instance ID or Instrument ID)
             const matchId = note.instanceId || note.instrumentId || note.role;
             if (id === instrument.instanceId || id === instrument.id || id === matchId) {
                  setLastTrigger(Date.now());
             }
        });
        return cleanup;
    }, [engine, instrument]);

    // Determine active events for this track
    const trackEvents = (events || []).filter(ev => {
        // UI Filter: Hide Keyswitches
        if (ev.d < 24 || ev.d > 108) return false;
        if (ev.isKeyswitch) return false;

        if (ev.instanceId === instrument.instanceId) return true;
        // Fallback for events generated before instance IDs were strictly enforced or if simple ID match
        if (ev.instrumentId === instrument.id) return true;
        if (instrument.roles && instrument.roles.includes(ev.role)) return true;
        return false;
    });

    // Color logic
    let color = '#00E5FF'; // Default Cyan
    const name = (instrument.name || instrument.id).toLowerCase();
    if (name.includes('bass')) color = '#4dabf5';
    else if (name.includes('kick')) color = '#ff1744';
    else if (name.includes('snare')) color = '#ff9100';
    else if (name.includes('hi-hat') || name.includes('hat')) color = '#ffea00';
    else if (name.includes('piano') || name.includes('keys')) color = '#b388ff';
    else if (name.includes('string') || name.includes('violin')) color = '#69f0ae';

    const renderMax = totalBeats || 32;

    return (
        <div style={{
            display: 'flex',
            height: '40px',
            background: 'transparent', // Transparent to allow Grid (z=0) to show through
            borderBottom: '1px solid #2a2b30',
            alignItems: 'center',
            position: 'relative', // Context for notes
            zIndex: 1 // Ensure above Grid
        }}>
            {/* Header */}
            <div style={{
                width: '200px',
                minWidth: '200px',
                boxSizing: 'border-box', // Ensure padding doesn't expand width
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                borderRight: '1px solid #333',
                background: '#15161a', // Opaque Header
                height: '100%',
                zIndex: 2
            }}>
                {/* Visual feedback linked to Note Listener */}
                <ActivityLED lastTrigger={lastTrigger} color={color} />

                <span style={{
                    fontSize: '0.75rem',
                    color: instrument.muted ? '#666' : '#eee',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginRight: '8px',
                    fontFamily: 'var(--font-tech)'
                }}>
                    {instrument.name || instrument.id}
                </span>

                <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                        title="Mute"
                        onClick={(e) => { e.stopPropagation(); onMute(instrument.instanceId); }}
                        style={{
                            background: instrument.muted ? '#ff4444' : '#222',
                            color: instrument.muted ? '#000' : '#666',
                            border: '1px solid #333',
                            borderRadius: '2px',
                            width: '20px',
                            height: '20px',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                        }}
                    >
                        M
                    </button>
                    <button
                        title="Solo"
                        onClick={(e) => { e.stopPropagation(); onSolo(instrument.instanceId); }}
                        style={{
                            background: instrument.soloed ? '#ffeb3b' : '#222',
                            color: instrument.soloed ? '#000' : '#666',
                            border: '1px solid #333',
                            borderRadius: '2px',
                            width: '20px',
                            height: '20px',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                             display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                        }}
                    >
                        S
                    </button>
                </div>
            </div>

            {/* Note Area */}
            <div style={{ flex: 1, position: 'relative', height: '100%', background: instrument.muted ? '#111' : 'transparent', zIndex: 1 }}>
                <svg width="100%" height="100%" preserveAspectRatio="none">
                    {trackEvents.map((ev, i) => {
                        if (typeof ev.s !== 'number') return null;
                        if (ev.muted) return null;

                        const xPct = (ev.s / renderMax) * 100;
                        const len = ev.len || 0.25;
                        const wPct = (len / renderMax) * 100;

                        const minP = 36, maxP = 96;
                        const note = Math.max(minP, Math.min(maxP, ev.d || 60));
                        const yPct = 100 - ((note - minP) / (maxP - minP) * 100);

                        return (
                            <rect
                                key={i}
                                x={`${xPct}%`}
                                y={`${yPct}%`}
                                width={`${Math.max(0.2, wPct)}%`}
                                height="15%"
                                fill={color}
                                fillOpacity={instrument.muted ? 0.2 : 0.8}
                                rx="1"
                            />
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

const PlaybackVisualizer = ({
    ensemble,
    engine,
    events,
    structure,
    getTransportTime,
    getTransportPosition,
    isPlaying,
    totalDuration,
    totalBeats,
    onSeek
}) => {
    const playheadRef = useRef(null);
    const containerRef = useRef(null);

    useAnimationFrame(() => {
        if (playheadRef.current) {
            let pct = 0;
            if (getTransportTime && totalDuration > 0) {
                const t = getTransportTime();

                const secondsPerBeat = (totalBeats > 0) ? (totalDuration / totalBeats) : 0.5;
                const offsetSeconds = secondsPerBeat * VISUAL_LATENCY_BEATS;

                const safeTime = Math.max(0, t + offsetSeconds);
                pct = (safeTime / totalDuration) * 100;
            }
            else if (getTransportPosition && totalBeats > 0) {
                const ticks = getTransportPosition();
                const currentBeat = ticks / TICKS_PER_BEAT;
                const correctedBeat = currentBeat + VISUAL_LATENCY_BEATS;
                pct = (correctedBeat / totalBeats) * 100;
            }
            playheadRef.current.style.left = `${Math.min(100, Math.max(0, pct))}%`;
        } else {
            if (playheadRef.current) playheadRef.current.style.left = '0%';
        }
    }, isPlaying);

    const handleSeek = (e) => {
        if (!onSeek || totalDuration <= 0) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const headerWidth = 200;
        const clickX = e.clientX - rect.left;

        if (clickX < headerWidth) return;

        const trackWidth = rect.width - headerWidth;
        const relativeX = clickX - headerWidth;
        const pct = Math.max(0, Math.min(1, relativeX / trackWidth));

        onSeek(pct * totalDuration);
    };

    if (!ensemble || !ensemble.activeInstruments) return null;

    return (
        <div
            className="playback-visualizer"
            onClick={handleSeek}
            ref={containerRef}
            style={{
                position: 'relative',
                background: '#0a0b10',
                border: '1px solid #333',
                borderRadius: '4px',
                overflow: 'hidden',
                cursor: 'pointer',
                marginBottom: '10px'
            }}
        >
            <div style={{ position: 'relative' }}>
                {/* 1. Background Grid Layer (z=0) */}
                <GridOverlay structure={structure} totalBeats={totalBeats} />

                {/* 2. Tracks Layer (z=1, transparent backgrounds) */}
                {ensemble.activeInstruments.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No instruments active. Add an instrument to see tracks.
                    </div>
                )}

                {ensemble.activeInstruments.map(inst => (
                    <TrackLane
                        key={inst.instanceId}
                        instrument={inst}
                        events={events}
                        totalBeats={totalBeats}
                        onMute={ensemble.toggleMute}
                        onSolo={ensemble.toggleSolo}
                        engine={engine}
                    />
                ))}

                {/* 3. Playhead Layer (z=10) */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '200px', // Matches header width
                    right: 0,
                    pointerEvents: 'none',
                    zIndex: 10
                }}>
                    <div
                        ref={playheadRef}
                        style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: '0%',
                            width: '2px',
                            background: '#fff',
                            boxShadow: '0 0 4px rgba(255,255,255,0.8)'
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default PlaybackVisualizer;
